/** biome-ignore-all lint/nursery/useExplicitType: Zod schema inference */
import { SOCIAL_PLATFORMS } from '@repo/config/social';
import { z } from 'zod';
import type { IAPI } from '../../apiResponse';
import { createApiResponseSchema } from '../../apiResponse';
import { socialVideoFormatSchema } from '../social-video-format-schema';

const detailChallengeSchema = z.object({
  id: z.number(),
  slug: z.string(),
  phrase: z.string(),
  imageUrl: z.string().nullable().default(null),
});

const detailClipSchema = z.object({
  clipId: z.number(),
  collectionId: z.number().nullable(),
  durationMs: z.number(),
  subtitleText: z.string(),
  clipUrl: z.string().nullable().default(null),
  collectionName: z.string(),
});

const detailPublishedChannelSchema = z.object({
  socialPostId: z.number().default(0),
  channelId: z.number(),
  platform: z.enum(SOCIAL_PLATFORMS),
  permalink: z.string().nullable(),
  status: z.enum(['queued', 'publishing', 'published', 'failed']),
  error: z.string().nullable().default(null),
});

const detailVideoSchema = z.object({
  id: z.number(),
  version: z.number(),
  format: socialVideoFormatSchema.optional().default('vertical'),
  status: z.enum(['generating', 'ready', 'failed']),
  url: z.string().nullable(),
  posterUrl: z.string().nullable(),
  description: z.string().nullable(),
  fileSizeKb: z.number().nullable(),
  durationMs: z.number().nullable(),
  error: z.string().nullable(),
  createdAt: z.number(),
  publishedChannels: z.array(detailPublishedChannelSchema).default([]),
});

const detailClipSelectionSchema = z.object({
  selectedClipIds: z.array(z.number()),
});

export type ISocialChallengeDetailChallenge = z.infer<typeof detailChallengeSchema>;
export type ISocialChallengeDetailClip = z.infer<typeof detailClipSchema>;
export type ISocialChallengeDetailPublishedChannel = z.infer<typeof detailPublishedChannelSchema>;
export type ISocialChallengeDetailVideo = z.infer<typeof detailVideoSchema>;
export type ISocialChallengeDetailClipSelection = z.infer<typeof detailClipSelectionSchema>;

const detailDataSchema = z.object({
  challenge: detailChallengeSchema,
  clips: z.array(detailClipSchema),
  videos: z.array(detailVideoSchema),
  clipSelection: detailClipSelectionSchema.nullable().default(null),
});

export interface ISocialChallengeDetailData {
  challenge: ISocialChallengeDetailChallenge;
  clips: ISocialChallengeDetailClip[];
  videos: ISocialChallengeDetailVideo[];
  clipSelection: ISocialChallengeDetailClipSelection | null;
}

export const getSocialChallengeDetailV1ResponseSchema = createApiResponseSchema(
  detailDataSchema,
) as z.ZodSchema<IAPI<ISocialChallengeDetailData>>;

export type IGetSocialChallengeDetailV1Response = IAPI<ISocialChallengeDetailData>;
